import React, { useEffect, useState } from 'react';
import { Button, Dropdown, DropdownItem, DropdownList } from '@cokitchen/cokitchen-components';
import LocalModal from 'components/base/local-modal';
import './index.scss';
import { FilterAltIcon } from 'cokitchen-icons';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import InfiniteScroll from 'react-infinite-scroll-component';
import { handleApiError } from 'utils/functions';
import { MemberListType } from 'models/member.model';
import { DepartmentType } from 'models/department.model';
type DefaultMembersType = {
  id: string;
  firstName: string;
  lastName: string;
};
type Props = {
  title?: string;
  show: boolean;
  onClose: () => void;
  maxAssignees?: number;
  handleClick: (members: MemberListType[]) => void;
  defaultMembers: DefaultMembersType[];
};
const AssignMealModal = ({
  title,
  show,
  onClose,
  maxAssignees,
  handleClick,
  defaultMembers
}: Props) => {
  const [searchText, setSearchText] = useState('');
  const [showList, setShowList] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [departments, setDepartments] = useState<DepartmentType[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [members, setMembers] = useState<MemberListType[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<MemberListType[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { activeCompany } = useDashboardContext();
  const companyId = activeCompany?.id;
  const getDepartments = async () => {
    if (!companyId) return;
    try {
      const res = await apiService.get(`department/company/${companyId}`);
      if (res) setDepartments(res?.data?.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  const getMembers = async () => {
    if (!companyId) return;
    if (page === 1) setLoading(true);
    const data = {
      company_id: companyId,
      department_id: selectedDepartment,
      search_text: searchText,
      page
    };

    try {
      const res = await apiService.get('member', { params: data });
      if (res) {
        setTotalItems(res?.data?.data?.total);
        page === 1
          ? setMembers(res?.data?.data?.results)
          : setMembers(members.concat(res?.data?.data?.results));
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSelectedMembers = () => {
    if (defaultMembers.length === 0) return;
    const selectedMembersList = members.filter((member) =>
      defaultMembers?.map((m) => m.id).includes(member.id)
    );
    setSelectedMembers(selectedMembersList);
  };

  useEffect(() => {
    getDefaultSelectedMembers();
  }, [members]);

  useEffect(() => {
    if (show) {
      getDepartments();
    }
  }, [companyId, show]);

  useEffect(() => {
    if (show) {
      getMembers();
    }
  }, [selectedDepartment, searchText, companyId, show, page]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, selectedMember: MemberListType) => {
    if (selectedMembers.length === maxAssignees) return;
    if (!e.target.checked) {
      setSelectedMembers(selectedMembers.filter((member) => member.id !== selectedMember.id));
    } else {
      setSelectedMembers([...selectedMembers, selectedMember]);
    }
  };

  const removeMember = (member: MemberListType) => {
    if (selectedMembers.includes(member))
      setSelectedMembers(selectedMembers.filter((selectedMember) => selectedMember !== member));
  };

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (page !== 1) setPage(1);
    setSearchText(e.target.value);
  };
  return (
    <LocalModal
      isOpen={show}
      title={title || 'Assign meal'}
      description={''}
      onRequestClose={() => onClose()}>
      <div className='mx-[-28px] mb-[-32px] assign__meal__modal'>
        <div className='pt-4 pb-3 px-6 bg-[#FAFAFA] border border-[#EAEBEB]'>
          <div className='flex items-center justify-between'>
            <div className='search__recipients__container border-0'>
              <input
                type='search'
                className='search__input text-sm text-[#5e6164] py-0 pr-0 pl-[26px]'
                placeholder='Search Recipients'
                value={searchText}
                onChange={handleSearchTextChange}
              />
            </div>
            <Dropdown
              visible={showList}
              onClickOutside={() => setShowList(false)}
              content={
                <div
                  className='flex justify-between items-center text-[#5E6164] text-sm gap-1 cursor-pointer'
                  onClick={() => setShowList(true)}>
                  <FilterAltIcon />
                  {filterValue || 'All departments'}
                </div>
              }>
              <DropdownList>
                {filterValue && (
                  <DropdownItem
                    onClick={() => {
                      setPage(1);
                      setFilterValue('');
                      setSelectedDepartment('');
                      setShowList(false);
                    }}>
                    All departments
                  </DropdownItem>
                )}
                {departments.map((department) => (
                  <DropdownItem
                    key={department.id}
                    onClick={() => {
                      setPage(1);
                      setFilterValue(department.name);
                      setSelectedDepartment(department.id);
                      setShowList(false);
                    }}>
                    {department.name}
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
          </div>
          {selectedMembers.length > 0 && (
            <div className='flex flex-wrap items-center gap-2 mt-4'>
              {selectedMembers.map((member) => (
                <div
                  key={member.id}
                  className='py-[6px] px-[10px] flex items-center justify-between font-medium text-base border border-[#E5E6E6] rounded-[20px]'>
                  <span className='mr-3'>
                    {member.user.first_name} {member.user.last_name}
                  </span>
                  <span role='button' onClick={() => removeMember(member)}>
                    x
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        {loading ? (
          <div className='text-center p-6 font-medium text-base'>Loading...</div>
        ) : members.length > 0 ? (
          <InfiniteScroll
            className='assign__meal__infinite__scroll'
            loader={<p className='text-sm flex justify-center mt-4'>Loading more members...</p>}
            dataLength={members.length}
            next={() => setPage(page + 1)}
            height={250}
            hasMore={totalItems > members.length}
            endMessage={
              <p style={{ textAlign: 'center' }} className='text-sm'>
                <b>Yay! You have seen it all</b>
              </p>
            }>
            {members.map((member) => (
              <div key={member.id} className='flex items-center justify-between mb-[18px]'>
                <span className='font-medium text-base'>
                  {member.user.first_name} {member.user.last_name}
                </span>
                <input
                  type='checkbox'
                  className='accent-[#e60a2b] w-5 h-5 rounded'
                  onChange={(e) => handleChange(e, member)}
                  checked={selectedMembers
                    .map((selectedMember) => selectedMember.id)
                    .includes(member.id)}
                />
              </div>
            ))}
          </InfiniteScroll>
        ) : (
          <div className='text-center p-6 font-medium text-base'>No Members</div>
        )}

        <div className='flex gap-3 items-center justify-between p-6 footer'>
          <Button
            onClick={onClose}
            className='w-full cancel__btn border-0 outline-0 text-[#2e3031] bg-[#f2f2f2]'
            color='transparent'
            small
            variant='block'>
            Cancel
          </Button>
          <Button
            className='w-full confirm__btn'
            color='primary'
            small
            variant='block'
            onClick={() => {
              handleClick(selectedMembers);
              onClose();
            }}>
            Confirm Order
          </Button>
        </div>
      </div>
    </LocalModal>
  );
};

export default AssignMealModal;
